import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Filter, Search, Plus, Check, X, Edit, Tag, FileText, Trash2, Shuffle, Mail, Infinity } from 'lucide-react';
import Header from '../../components/ui/Header';

import { Button } from '../../components/ui/Button';
import * as partnerService from '../../services/partnerService';
import { supabase } from '../../lib/supabase';
import { PASSWORD_RECOVERY_REDIRECT } from '../../config/authUrls';

interface Partner {
  id: string;
  partner_code?: string;
  business_name: string;
  contact_email: string;
  contact_phone?: string;
  contact_name?: string;
  billing_address?: string;
  business_address?: string;
  vat_number?: string;
  status: 'pending' | 'active' | 'suspended' | 'rejected';
  created_at?: string;
  max_offers_allowed?: number | null; // Allow null for unlimited
}

const PartnerManagementDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [totalOffers, setTotalOffers] = useState(0);
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null);
  const [newPartner, setNewPartner] = useState({
    contact_email: '',
    partner_password: '',
    contact_name: '',
    business_name: '',
    billing_address: '',
    vat_number: '',
    contact_phone: '',
    business_address: ''
  });
  const [editingPartnerId, setEditingPartnerId] = useState<string | null>(null);
  const [editingMaxOffers, setEditingMaxOffers] = useState<number>(5);

  // Load partners and total offers
  useEffect(() => {
    loadPartners();
    loadTotalOffers();
  }, []);

  const loadPartners = async () => {
    try {
      setLoading(true);
      const data = await partnerService.getAllPartners();
      setPartners(data);
    } catch (error) {
      console.error('Error loading partners:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTotalOffers = async () => {
    try {
      const count = await partnerService.getTotalOffersCount();
      setTotalOffers(count);
    } catch (error) {
      console.error('Error loading total offers:', error);
    }
  };

  // NEW: Handle updating partner max offers
  const handleUpdateMaxOffers = async (partnerId: string, maxOffers: number) => {
    try {
      const { error } = await supabase
        .from('partners')
        .update({ max_offers_allowed: maxOffers })
        .eq('id', partnerId);

      if (error) throw error;

      alert('Partner offer limit updated successfully');
      setEditingPartnerId(null);
      loadPartners(); // Reload partners
    } catch (error) {
      console.error('Error updating partner max offers:', error);
      alert('Failed to update partner offer limit. Please try again.');
    }
  };

  const startEditingMaxOffers = (partnerId: string, currentMax: number) => {
    setEditingPartnerId(partnerId);
    setEditingMaxOffers(currentMax);
  };

  const cancelEditingMaxOffers = () => {
    setEditingPartnerId(null);
    setEditingMaxOffers(5);
  };

  // KPI calculations - UPDATED to use correct source for active partners
  const totalPartners = partners.length;
  const activePartners = partners.filter(p => p.status === 'active').length;
  const suspendedPartners = partners.filter(p => p.status === 'suspended').length;
  const pendingPartners = partners.filter(p => p.status === 'pending').length;

  // Filter partners
  const filteredPartners = partners.filter(partner => {
    const matchesSearch = 
      partner.business_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      partner.contact_email.toLowerCase().includes(searchQuery.toLowerCase());
    // Note: Status filter only applies to 'active', 'suspended', 'rejected' now
    // 'pending' and 'pre_approved' won't appear here
    const matchesStatus = statusFilter === 'all' || partner.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // CRITICAL: Sort partners to ensure PART-6329 (admin partner) is always on top
  const sortedPartners = [...filteredPartners].sort((a, b) => {
    const isAdminPartnerA = a.partner_code === 'PART-6329' || a.contact_email === 'partner@travelheroes.at';
    const isAdminPartnerB = b.partner_code === 'PART-6329' || b.contact_email === 'partner@travelheroes.at';
    
    // Admin partner always goes to the top
    if (isAdminPartnerA && !isAdminPartnerB) return -1;
    if (!isAdminPartnerA && isAdminPartnerB) return 1;
    
    // For other partners, maintain original order
    return 0;
  });

  // Helper function to check if a partner is the admin system partner
  const isAdminSystemPartner = (partner: Partner): boolean => {
    return partner.partner_code === 'PART-6329' || partner.contact_email === 'partner@travelheroes.at';
  };

  // Create partner
  const handleCreatePartner = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Disable submit button to prevent double-clicks
    const submitButton = e.currentTarget.querySelector('button[type="submit"]') as HTMLButtonElement;
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Creating...';
    }

    try {
      const result = await partnerService.createPartner(newPartner);
      
      // CRITICAL FIX: Check if partner was returned (success or existing account)
      if (result.partner) {
        // Close modal and reset form
        setShowCreateModal(false);
        setNewPartner({
          contact_email: '',
          partner_password: '',
          contact_name: '',
          business_name: '',
          billing_address: '',
          vat_number: '',
          contact_phone: '',
          business_address: ''
        });
        
        // Reload partners list
        loadPartners();
        
        // Show appropriate success message
        if (result.error === null) {
          // Genuinely new partner created
          alert('✅ Partner created successfully! The partner account is now active and ready to use.');
        } else {
          // Existing partner account found and activated
          alert('✅ Partner account already exists! The partner has been activated and can log in with existing credentials.');
        }
      } else if (result.error) {
        // Display the error message from the service
        alert(`❌ Partner Creation Failed\n\n${result.error}\n\nPlease verify the information and try again.`);
      } else {
        // Unexpected case - no partner and no error
        alert('❌ Partner Creation Failed\n\nAn unexpected error occurred. Please try again.');
      }
    } catch (error) {
      console.error('Error creating partner:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      alert(`❌ Partner Creation Failed\n\n${errorMessage}\n\nPlease try again or contact support if the issue persists.`);
    } finally {
      // Re-enable submit button
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = 'Create Partner';
      }
    }
  };

  // Edit partner handler
  const handleEditPartner = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPartner) return;

    try {
      await partnerService.updatePartner(editingPartner.id, {
        business_name: editingPartner.business_name,
        contact_name: editingPartner.contact_name,
        contact_email: editingPartner.contact_email,
        contact_phone: editingPartner.contact_phone,
        business_address: editingPartner.business_address,
        billing_address: editingPartner.billing_address,
        vat_number: editingPartner.vat_number
      });
      setShowEditModal(false);
      setEditingPartner(null);
      loadPartners();
    } catch (error) {
      console.error('Error updating partner:', error);
      alert('Failed to update partner. Please try again.');
    }
  };

  // Delete partner handler
  const handleDeletePartner = async (partnerId: string) => {
    // Find the partner to check if it's the admin system partner
    const partner = partners.find(p => p.id === partnerId);
    
    // CRITICAL: Prevent deletion of admin system partner
    if (partner && isAdminSystemPartner(partner)) {
      alert('⚠️ Cannot delete system admin partner (PART-6329)\n\nThis is the system partner used for all admin-generated offers and cannot be removed.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this partner? This will also delete all associated offers and data. This action cannot be undone.')) {
      return;
    }

    console.log('Attempting to delete partner:', partnerId);

    try {
      const result = await partnerService.deletePartner(partnerId);

      if (result.success) {
        // Refresh the partners list
        await loadPartners();
        alert('Partner deleted successfully');
      } else {
        console.error('Delete failed:', result.error);
        alert(`Failed to delete partner: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting partner:', error);
      alert('An unexpected error occurred while deleting the partner');
    }
  };

  // NEW: Send password recovery email handler
  const handleSendPasswordRecovery = async (partnerEmail: string, partnerName: string) => {
    if (!window.confirm(`Send password recovery email to ${partnerName} (${partnerEmail})?`)) {
      return;
    }

    try {
      console.log('Sending password recovery email to:', partnerEmail);
      console.log('Recovery redirect URL:', PASSWORD_RECOVERY_REDIRECT);

      const { error } = await supabase.auth.resetPasswordForEmail(partnerEmail, {
        redirectTo: PASSWORD_RECOVERY_REDIRECT,
      });

      if (error) {
        console.error('Error sending password recovery email:', error);
        alert(`Failed to send password recovery email: ${error.message}`);
        return;
      }

      alert(`✅ Password recovery email sent successfully to ${partnerEmail}\n\nThe partner will receive an email with a link to reset their password.`);
    } catch (error) {
      console.error('Error sending password recovery email:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      alert(`❌ Failed to send password recovery email\n\n${errorMessage}`);
    }
  };

  // Open edit modal
  const openEditModal = (partner: Partner) => {
    setEditingPartner(partner);
    setShowEditModal(true);
  };

  // Navigate to application workflow
  const handleViewApplication = (partnerId: string) => {
    navigate('/partner-onboarding-workflow', { state: { selectedPartnerId: partnerId } });
  };

  // Activate partner
  const handleActivatePartner = async (partnerId: string) => {
    try {
      await partnerService.activatePartner(partnerId);
      loadPartners();
    } catch (error) {
      console.error('Error activating partner:', error);
      alert('Failed to activate partner.');
    }
  };

  // Suspend partner
  const handleSuspendPartner = async (partnerId: string) => {
    if (window.confirm('Are you sure you want to suspend this partner?')) {
      try {
        await partnerService.suspendPartner(partnerId);
        loadPartners();
      } catch (error) {
        console.error('Error suspending partner:', error);
        alert('Failed to suspend partner.');
      }
    }
  };

  // Generate random partner data for development
  const generateRandomPartnerData = () => {
    const randomBusinesses = [
      'Alpine Ski Resort', 'Vienna Coffee House', 'Danube River Tours', 'Schönbrunn Palace Shop', 'Mozart Concert Hall', 'Austrian Wine Cellar','Salzburg Mountain Guides', 'Innsbruck Adventure Park', 'Graz Art Gallery','Tyrol Hiking Adventures', 'Vienna Opera Boutique', 'Austrian Chocolate Factory'
    ];
    
    const randomNames = [
      'Hans Müller', 'Anna Schmidt', 'Franz Weber', 'Maria Bauer',
      'Stefan Fischer', 'Elisabeth Wagner', 'Michael Schneider', 'Julia Hoffmann'
    ];
    
    const randomStreets = [
      'Hauptstraße', 'Kirchgasse', 'Bahnhofstraße', 'Marktplatz',
      'Ringstraße', 'Schulgasse', 'Dorfstraße', 'Stadtplatz'
    ];
    
    const randomCities = [
      'Vienna', 'Salzburg', 'Innsbruck', 'Graz', 'Linz', 'Klagenfurt'
    ];
    
    // Generate secure random password
    const generatePassword = () => {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
      let password = '';
      for (let i = 0; i < 12; i++) {
        password += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return password;
    };
    
    const business = randomBusinesses[Math.floor(Math.random() * randomBusinesses.length)];
    const contactName = randomNames[Math.floor(Math.random() * randomNames.length)];
    const street = randomStreets[Math.floor(Math.random() * randomStreets.length)];
    const city = randomCities[Math.floor(Math.random() * randomCities.length)];
    const streetNumber = Math.floor(Math.random() * 200) + 1;
    const postalCode = Math.floor(Math.random() * 9000) + 1000;
    const phoneNumber = `+43 ${Math.floor(Math.random() * 900) + 100} ${Math.floor(Math.random() * 9000000) + 1000000}`;
    const vatNumber = `ATU${Math.floor(Math.random() * 90000000) + 10000000}`;
    const emailDomain = business.toLowerCase().replace(/\s+/g, '');
    const email = `${contactName.toLowerCase().replace(/\s+/g, '.')}@${emailDomain}.at`;
    
    setNewPartner({
      business_name: business,
      contact_name: contactName,
      contact_email: email,
      contact_phone: phoneNumber,
      business_address: `${street} ${streetNumber}, ${postalCode} ${city}`,
      billing_address: `${street} ${streetNumber}, ${postalCode} ${city}`,
      vat_number: vatNumber,
      partner_password: generatePassword()
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Partner Management</h1>
              <p className="text-gray-600">Manage business partners and their partnerships</p>
            </div>
          </div>
        </div>

        {/* KPI Cards - UPDATED with Suspended Partners card */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Partners</p>
                <p className="text-2xl font-bold text-gray-900">{totalPartners}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">All statuses included</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Active Partnerships</p>
                <p className="text-2xl font-bold text-green-600">{activePartners}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <Check className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Suspended Partners</p>
                <p className="text-2xl font-bold text-red-600">{suspendedPartners}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <X className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Offers</p>
                <p className="text-2xl font-bold text-indigo-600">{totalOffers}</p>
              </div>
              <div className="p-3 bg-indigo-100 rounded-full">
                <Tag className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search partners..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full md:w-64"
                />
              </div>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <Button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 whitespace-nowrap"
            >
              <Plus className="w-4 h-4" />
              Add Partner
            </Button>
          </div>
        </div>

        {/* Partners Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Partner ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Business
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Max Offers
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                      Loading partners...
                    </td>
                  </tr>
                ) : sortedPartners.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                      No partners found
                    </td>
                  </tr>
                ) : (
                  sortedPartners.map((partner) => {
                    const isSystemAdmin = isAdminSystemPartner(partner);
                    
                    return (
                      <tr key={partner.id} className={`hover:bg-gray-50 ${isSystemAdmin ? 'bg-blue-50' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-semibold text-blue-600">
                              {partner.partner_code || partner.id}
                            </div>
                            {isSystemAdmin && (
                              <span className="px-2 py-1 text-xs font-semibold text-white bg-purple-600 rounded-full">
                                SYSTEM
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {partner.business_name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{partner.contact_email}</div>
                          {partner.contact_phone && (
                            <div className="text-xs text-gray-500">{partner.contact_phone}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                            ${partner.status === 'active' ? 'bg-green-100 text-green-800' : ''}
                            ${partner.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : ''}
                            ${partner.status === 'suspended' ? 'bg-red-100 text-red-800' : ''}
                            ${partner.status === 'rejected' ? 'bg-gray-100 text-gray-800' : ''}
                          `}>
                            {partner.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {isSystemAdmin ? (
                            <div className="flex items-center gap-2">
                              <Infinity className="w-5 h-5 text-purple-600" />
                              <span className="text-sm font-medium text-gray-900">Unlimited</span>
                            </div>
                          ) : editingPartnerId === partner.id ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="1"
                                max="100"
                                value={editingMaxOffers}
                                onChange={(e) => setEditingMaxOffers(parseInt(e.target.value) || 1)}
                                className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                                autoFocus
                              />
                              <button
                                onClick={() => handleUpdateMaxOffers(partner.id, editingMaxOffers)}
                                className="text-green-600 hover:text-green-900 text-xs font-medium"
                              >
                                Save
                              </button>
                              <button
                                onClick={cancelEditingMaxOffers}
                                className="text-gray-600 hover:text-gray-900 text-xs font-medium"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">
                                {partner.max_offers_allowed ?? 2}
                              </span>
                              <button
                                onClick={() => startEditingMaxOffers(partner.id, partner.max_offers_allowed ?? 2)}
                                className="text-blue-600 hover:text-blue-900 text-xs"
                                title="Edit Max Offers"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {isSystemAdmin ? (
                            <div className="flex items-center text-xs text-gray-500">
                              <span>No actions available</span>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => openEditModal(partner)}
                                className="text-blue-600 hover:text-blue-900"
                                title="Edit"
                              >
                                <Edit className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleSendPasswordRecovery(partner.contact_email, partner.business_name)}
                                className="text-indigo-600 hover:text-indigo-900"
                                title="Send Password Reset Email"
                              >
                                <Mail className="w-5 h-5" />
                              </button>
                              {partner.status === 'pending' && (
                                <>
                                  <button
                                    onClick={() => handleViewApplication(partner.id)}
                                    className="text-purple-600 hover:text-purple-900"
                                    title="View Application"
                                  >
                                    <FileText className="w-5 h-5" />
                                  </button>
                                  <button
                                    onClick={() => handleActivatePartner(partner.id)}
                                    className="text-green-600 hover:text-green-900"
                                    title="Activate"
                                  >
                                    <Check className="w-5 h-5" />
                                  </button>
                                </>
                              )}
                              {partner.status === 'active' && (
                                <button
                                  onClick={() => handleSuspendPartner(partner.id)}
                                  className="text-orange-600 hover:text-orange-900"
                                  title="Suspend"
                                >
                                  <X className="w-5 h-5" />
                                </button>
                              )}
                              {partner.status === 'suspended' && (
                                <button
                                  onClick={() => handleActivatePartner(partner.id)}
                                  className="text-green-600 hover:text-green-900"
                                  title="Unsuspend"
                                >
                                  <Check className="w-5 h-5" />
                                </button>
                              )}
                              <button
                                onClick={() => handleDeletePartner(partner.id)}
                                className="text-red-600 hover:text-red-900 transition-colors"
                                title="Delete Partner"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create Partner Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Add New Partner</h2>
              <button
                type="button"
                onClick={generateRandomPartnerData}
                className="flex items-center gap-2 px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium"
                title="Fill with random data (Dev)"
              >
                <Shuffle className="w-4 h-4" />
                Random Info
              </button>
            </div>
            <form onSubmit={handleCreatePartner} className="space-y-4">
              {/* Business Information Section */}
              <div className="border-b pb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Business Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Business Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={newPartner.business_name}
                      onChange={(e) => setNewPartner({ ...newPartner, business_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Business Address
                    </label>
                    <input
                      type="text"
                      value={newPartner.business_address}
                      onChange={(e) => setNewPartner({ ...newPartner, business_address: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Billing Information Section */}
              <div className="border-b pb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Billing Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Billing Address
                    </label>
                    <input
                      type="text"
                      value={newPartner.billing_address}
                      onChange={(e) => setNewPartner({ ...newPartner, billing_address: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      VAT Number
                    </label>
                    <input
                      type="text"
                      value={newPartner.vat_number}
                      onChange={(e) => setNewPartner({ ...newPartner, vat_number: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., AT12345678"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information Section */}
              <div className="border-b pb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={newPartner.contact_name}
                      onChange={(e) => setNewPartner({ ...newPartner, contact_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Primary contact person"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={newPartner.contact_email}
                      onChange={(e) => setNewPartner({ ...newPartner, contact_email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Number
                    </label>
                    <input
                      type="tel"
                      value={newPartner.contact_phone}
                      onChange={(e) => setNewPartner({ ...newPartner, contact_phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="+43 1 234 5678"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password *
                    </label>
                    <input
                      type="password"
                      required
                      value={newPartner.partner_password}
                      onChange={(e) => setNewPartner({ ...newPartner, partner_password: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Auto-generated when using random data"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 bg-gray-200 text-gray-800 hover:bg-gray-300"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                >
                  Create Partner
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Partner Modal */}
      {showEditModal && editingPartner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Edit Partner</h2>
            <form onSubmit={handleEditPartner} className="space-y-4">
              {/* Business Information Section */}
              <div className="border-b pb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Business Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Business Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={editingPartner.business_name}
                      onChange={(e) => setEditingPartner({ ...editingPartner, business_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Business Address
                    </label>
                    <input
                      type="text"
                      value={editingPartner.business_address || ''}
                      onChange={(e) => setEditingPartner({ ...editingPartner, business_address: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Billing Information Section */}
              <div className="border-b pb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Billing Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Billing Address
                    </label>
                    <input
                      type="text"
                      value={editingPartner.billing_address || ''}
                      onChange={(e) => setEditingPartner({ ...editingPartner, billing_address: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      VAT Number
                    </label>
                    <input
                      type="text"
                      value={editingPartner.vat_number || ''}
                      onChange={(e) => setEditingPartner({ ...editingPartner, vat_number: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., AT12345678"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information Section */}
              <div className="border-b pb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={editingPartner.contact_name || ''}
                      onChange={(e) => setEditingPartner({ ...editingPartner, contact_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Primary contact person"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={editingPartner.contact_email}
                      onChange={(e) => setEditingPartner({ ...editingPartner, contact_email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Number
                    </label>
                    <input
                      type="tel"
                      value={editingPartner.contact_phone || ''}
                      onChange={(e) => setEditingPartner({ ...editingPartner, contact_phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="+43 1 234 5678"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingPartner(null);
                  }}
                  className="flex-1 bg-gray-200 text-gray-800 hover:bg-gray-300"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                >
                  Save Changes
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerManagementDashboard;